import React, { useState, useEffect } from 'react';
import { Clock, Loader2, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import { AnalysisService } from '../services/analysisService';

interface JobProgressProps {
  jobId: string;
  onComplete: (result: any) => void;
  onCancel: () => void;
}

interface JobStatus {
  id: string;
  status: 'starting' | 'transcribing' | 'analyzing' | 'completed' | 'failed';
  progress: {
    stage: string;
    message: string;
    elapsedTime: number;
  };
  result?: any;
  error?: string;
  createdAt: string;
  completedAt?: string;
}

export const JobProgress: React.FC<JobProgressProps> = ({ jobId, onComplete, onCancel }) => {
  const [jobStatus, setJobStatus] = useState<JobStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState<number>(0);

  useEffect(() => {
    if (!jobId) return;

    const pollJobStatus = async () => {
      try {
        const status: JobStatus = await AnalysisService.getJobStatus(jobId);
        setJobStatus(status);
        setElapsedSeconds(status.progress.elapsedTime);
        
        // If completed, notify parent
        if (status.status === 'completed' && status.result) {
          onComplete(status.result);
          return;
        }
        
        // If failed, show error
        if (status.status === 'failed') {
          setError(status.error || 'Zadanie nie powiodło się z nieznanym błędem');
          return;
        }
        
      } catch (err) {
        console.error('Failed to poll job status:', err);
        setError(err instanceof Error ? err.message : 'Nie udało się sprawdzić statusu zadania');
      }
    };

    // Poll immediately
    pollJobStatus();
    
    // Continue polling every 10 seconds until completed or failed
    const interval = setInterval(() => {
      if (jobStatus?.status === 'completed' || jobStatus?.status === 'failed') {
        clearInterval(interval);
        return;
      }
      pollJobStatus();
    }, 10000); // Poll every 10 seconds

    return () => clearInterval(interval);
  }, [jobId, jobStatus?.status]);

  // Local timer that increments every second for smooth UI
  useEffect(() => {
    if (!jobStatus || jobStatus.status === 'completed' || jobStatus.status === 'failed') {
      return;
    }

    const timer = setInterval(() => {
      setElapsedSeconds(prev => prev + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [jobStatus?.status]);

  const formatTime = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const getStatusIcon = () => {
    if (!jobStatus) return <Loader2 className="w-6 h-6 animate-spin text-primary-500" />;
    
    switch (jobStatus.status) {
      case 'completed':
        return <CheckCircle className="w-6 h-6 text-green-500" />;
      case 'failed':
        return <AlertCircle className="w-6 h-6 text-red-500" />;
      default:
        return <Loader2 className="w-6 h-6 animate-spin text-primary-500" />;
    }
  };

  const getProgressPercent = (): number => {
    if (!jobStatus) return 0;
    
    switch (jobStatus.status) {
      case 'starting': return 5;
      case 'transcribing': return 20 + Math.min(70, Math.floor(elapsedSeconds / 15)); // Up to 90% for transcribing
      case 'analyzing': return 95;
      case 'completed': return 100;
      case 'failed': return 100;
      default: return 0;
    }
  };

  const getStatusMessage = (): string => {
    if (error) return error;
    if (!jobStatus) return 'Łączenie...';
    
    return jobStatus.progress.message || 'Przetwarzanie...';
  };

  const getStageLabel = (): string => {
    if (!jobStatus) return 'Inicjalizacja';
    
    switch (jobStatus.status) {
      case 'starting': return 'Uruchamianie';
      case 'transcribing': return 'Transkrypcja & Analiza';
      case 'analyzing': return 'Analizowanie transkryptu'; 
      case 'completed': return 'Zakończono';
      case 'failed': return 'Nie powiodło się';
      default: return 'Przetwarzanie';
    }
  };

  if (error) {
    return (
      <div className="animate-slide-up">
        <div className="glass-card rounded-2xl p-8 text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">
            Przetwarzanie nie powiodło się
          </h2>
          <p className="text-red-600 dark:text-red-400 mb-6">
            {error}
          </p>
          <button
            onClick={onCancel}
            className="flex items-center space-x-2 mx-auto px-6 py-3 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 
                       text-gray-700 dark:text-gray-300 rounded-xl transition-colors duration-200"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Spróbuj ponownie</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-slide-up">
      <div className="glass-card rounded-2xl p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            {getStatusIcon()}
          </div>
          <h2 className="text-2xl font-bold gradient-text mb-2">
            {getStageLabel()}
          </h2>
          <p className="text-gray-600 dark:text-gray-300">
            {getStatusMessage()}
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
              Postęp
            </span>
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
              {getProgressPercent()}%
            </span>
          </div>
          <div className="bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${getProgressPercent()}%` }}
            ></div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-6 mb-8">
          <div className="text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
            <Clock className="w-6 h-6 text-gray-500 dark:text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Upłynął czas</p>
            <p className="text-lg font-semibold text-gray-800 dark:text-gray-200">
              {formatTime(elapsedSeconds)}
            </p>
          </div>
          <div className="text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
            <Loader2 className="w-6 h-6 text-gray-500 dark:text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Status</p>
            <p className="text-lg font-semibold text-gray-800 dark:text-gray-200">
              {jobStatus?.status || 'connecting'}
            </p>
          </div>
        </div>

        {/* Info */}
        <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4 mb-6">
          <div className="flex items-start space-x-3">
            <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center mt-0.5">
              <span className="text-white text-xs font-bold">i</span>
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-1">
                Transkrypcja audio w toku
              </h4>
              <p className="text-blue-700 dark:text-blue-300 text-sm">
                Transkrypcja AI zazwyczaj trwa około 3 razy dłużej niż długość pliku audio. Wyniki analizy pojawią się automatycznie po zakończeniu.
                Możesz zminimalizować tą kartę podczas oczekiwania.
              </p>
            </div>
          </div>
        </div>

        {/* Cancel Button */}
        <div className="text-center">
          <button
            onClick={onCancel}
            className="flex items-center space-x-2 mx-auto px-6 py-3 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 
                       text-gray-700 dark:text-gray-300 rounded-xl transition-colors duration-200"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Anuluj i wróć</span>
          </button>
        </div>
      </div>
    </div>
  );
};